import { useEffect, useState } from "react";
import { LUMEN_PUBLIC } from "@/lib/flags";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "@fontsource-variable/cormorant";
import { useAuth } from "@/lib/auth";

const navLink =
  "inline-flex min-h-[44px] items-center text-[#F5F5F7]/70 transition-colors duration-200 hover:text-[#84DFF4]";

// The menu is the floor plan: every deep route filed under its room. The Rail carries the rooms themselves.
const GROUPS = [
  { numeral: "I", room: "PrismPixel", links: [
    { to: "/#room-prismpixel", label: "The opening", testid: "header-link-opening" },
    { to: "/about", label: "The story", testid: "header-link-story" },
    { to: "/document", label: "Foundational document", testid: "header-link-document" },
  ] },
  { numeral: "II · III", room: "Glass · Glow", links: [
    { to: "/technology", label: "Technology", testid: "header-link-technology" },
    { to: "/formulations", label: "Formulations", testid: "header-link-formulations" },
    { to: "/technology/projector-advisor", label: "Projector advisor", testid: "header-link-advisor" },
    { to: "/topography", label: "Solar Shadow Forecaster", testid: "header-link-topography" },
  ] },
  { numeral: "IV", room: "Creators", links: [
    { to: "/retrospective", label: "Photographs (2015 → today)", testid: "header-link-retrospective" },
    { to: "/films", label: "Videos (2015 → today)", testid: "header-link-films" },
    { to: "/makers", label: "Makers", testid: "header-link-makers" },
    { to: "/commons", label: "The commons", testid: "header-link-commons" },
    { to: "/prism", label: "PrismIntelligence Creator™", testid: "header-link-prism" },
    { to: "/patron", label: "Patron access", testid: "header-link-shop" },
    { to: "/collaborate", label: "Collaborate", testid: "header-link-collaborate" },
  ] },
  { numeral: "V", room: "Laboratory", links: [
    { to: "/lab", label: "Lab records", testid: "header-link-lab" },
    { to: "/knowledge", label: "Knowledge", testid: "header-link-knowledge" },
    { to: "/archive", label: "The inverse archive", testid: "header-link-archive" },
    { to: "/threshold", label: "Open questions · threshold", testid: "header-link-threshold" },
  ] },
];

// Sister mark: LUMEN CONTINUUM stands beside PRISMPIXEL™, a step quieter, in its own serif.
// On fine pointers a small Veil Wall concept surfaces beneath it — the entrance reads as a doorway.
function LumenMark({ mobile = false, onClick }) {
  if (!LUMEN_PUBLIC) return null;
  return (
    <span className={`${mobile ? "flex" : "hidden sm:flex"} relative shrink-0 items-center whitespace-nowrap`}>
      <span aria-hidden="true" className="mx-3 h-4 w-px bg-[#8E8E93]/45 sm:mx-4" />
      <Link
        data-testid={mobile ? "header-lumen-mark-mobile" : "header-lumen-mark"}
        to="/lumen"
        onClick={onClick}
        aria-label="LUMEN CONTINUUM — architectural art in light"
        className="group inline-flex min-h-[44px] items-center gap-2 text-[#F5F5F7]/75 transition-colors duration-200 hover:text-[#84DFF4]"
        style={{ fontFamily: '"Newsreader Variable", Georgia, serif' }}
      >
        <span className="text-[0.95rem] font-light tracking-[0.14em] min-[1400px]:text-[1.05rem]">LUMEN<span className="text-[#F5F5F7]/45 transition-colors duration-200 group-hover:text-[#84DFF4]/70 xl:hidden min-[1400px]:inline"> CONTINUUM</span></span>
        <span aria-hidden="true" className="text-[0.7rem] text-[#84DFF4]/60 transition-transform duration-300 group-hover:translate-x-0.5">↗</span>
        {!mobile && (
          <span
            aria-hidden="true"
            data-testid="header-lumen-doorway"
            className="pointer-events-none absolute left-8 top-full hidden w-[232px] origin-top-left translate-y-1 scale-[0.97] border border-[#8E8E93]/35 bg-[#030305] p-1.5 opacity-0 shadow-[0_24px_60px_rgba(0,0,0,0.7)] transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-2 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:translate-y-2 group-focus-visible:scale-100 group-focus-visible:opacity-100 [@media(pointer:fine)]:block"
          >
            <img src="/lumen/img/veil-thumb.webp" alt="" width={320} height={200} loading="lazy" decoding="async" className="block aspect-[8/5] w-full object-cover" />
            <span className="flex items-baseline justify-between gap-3 px-1.5 pb-1 pt-2">
              <span className="font-mono-spec text-[0.5rem] uppercase tracking-[0.26em] text-[#8E8E93]/80">Veil Wall · concept</span>
              <span className="font-mono-spec text-[0.5rem] uppercase tracking-[0.2em] text-[#F5F5F7]/45">Enter →</span>
            </span>
          </span>
        )}
      </Link>
    </span>
  );
}

function Avatar({ user }) {
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name || user.email}
        referrerPolicy="no-referrer"
        className="h-7 w-7 rounded-full border border-[#8E8E93]/40 object-cover"
      />
    );
  }
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[#8E8E93]/40 text-xs text-[#84DFF4]">
      {initial}
    </span>
  );
}

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const auth = useAuth();
  const user = auth?.user;
  const { pathname } = useLocation();
  const onLumen = pathname === "/lumen" || pathname.startsWith("/lumen/");

  useEffect(() => {
    if (!open) return;
    const lenis = window.__lenis;
    lenis?.stop?.();
    document.body.style.overflow = "hidden";
    document.documentElement.classList.add("menu-open");
    return () => {
      document.body.style.overflow = "";
      document.documentElement.classList.remove("menu-open");
      lenis?.start?.();
    };
  }, [open]);

  useEffect(() => {
    if (onLumen) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrolled(window.scrollY > 40);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [onLumen]);

  // LUMEN CONTINUUM carries its own navigation; the Prism Pixel header steps aside there.
  if (onLumen) return null;

  const AuthControl = ({ mobile = false }) =>
    user === undefined ? null : user ? (
      <span className="flex items-center gap-3">
        <Link data-testid={mobile ? "header-mobile-profile" : "header-profile"} to="/commons" onClick={() => setOpen(false)} className="flex items-center gap-2" title={user.name || user.email}>
          <Avatar user={user} />
          <span className="font-mono-spec text-[0.6rem] text-[#F5F5F7]/60">{(user.name || user.email || "").split(" ")[0]}</span>
        </Link>
        <button
          data-testid={mobile ? "header-mobile-signout" : "header-signout"}
          onClick={() => { setOpen(false); auth.logout(); }}
          className="font-mono-spec text-[0.6rem] text-[#F5F5F7]/45 transition-colors duration-200 hover:text-[#84DFF4]"
        >
          Sign out
        </button>
      </span>
    ) : (
      <button
        data-testid={mobile ? "header-mobile-signin" : "header-signin"}
        onClick={() => { setOpen(false); auth.login(); }}
        className="inline-flex min-h-[44px] items-center gap-2 font-mono-spec text-[0.56rem] tracking-[0.26em] text-[#F5F5F7]/45 transition-colors duration-300 hover:text-[#84DFF4]"
      >
        <span aria-hidden="true" className="h-px w-5 bg-[#8E8E93]/35" />
        Founders · sign in
      </button>
    );

  return (
    <header
      data-testid="site-header"
      style={{ top: "var(--whisper-offset, 0px)" }}
      className={`fixed inset-x-0 z-50 flex items-center justify-between px-6 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] transition-[background-color] duration-700 md:px-10 ${
        scrolled || open ? "bg-[#030305]/95" : "bg-transparent"
      }`}
    >
      <div className="flex shrink-0 items-center">
        <Link
          data-testid="header-wordmark"
          to="/"
          aria-label="Prism Pixel — home"
          className="inline-flex min-h-[44px] items-center font-display text-lg font-medium tracking-[0.22em] text-[#F5F5F7] transition-colors duration-200 hover:text-[#84DFF4] sm:text-xl"
        >
          PRISMPIXEL<span className="-ml-[0.12em] align-super text-[0.42em] font-normal tracking-normal">™</span>
        </Link>
        <LumenMark />
      </div>

      {/* The header steps back: wordmark, one door, the floor plan. The Rail carries the rooms. */}
      <nav data-testid="header-nav" aria-label="Primary" className="flex items-center gap-2 sm:gap-5">
        <Link data-testid="header-link-collaborate-top" to="/collaborate" className={`${navLink} hidden font-mono-spec text-[0.6rem] tracking-[0.26em] sm:inline-flex`}>Collaborate</Link>
        <button
          data-testid="header-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex h-11 items-center justify-center gap-2 px-1 text-[#F5F5F7] transition-colors duration-200 hover:text-[#84DFF4]"
        >
          <span className="hidden font-mono-spec text-[0.6rem] tracking-[0.26em] sm:inline">{open ? "Close" : "Menu"}</span>
          {open ? <X size={22} strokeWidth={2.25} /> : <Menu size={22} strokeWidth={2.25} />}
        </button>
      </nav>

      {/* The floor plan — full-screen, every breakpoint */}
      {open && (
        <div
          data-testid="header-mobile-menu"
          data-lenis-prevent
          className="fixed inset-0 z-[60] flex h-[100dvh] flex-col overflow-y-auto overscroll-contain bg-[#030305] [-webkit-overflow-scrolling:touch]"
        >
          <div className="flex shrink-0 items-center justify-between px-6 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <div className="flex min-w-0 items-center">
              <Link
                data-testid="header-mobile-wordmark"
                to="/"
                onClick={() => setOpen(false)}
                className="font-display text-lg font-medium tracking-[0.22em] text-[#F5F5F7]"
              >
                PRISMPIXEL<span className="-ml-[0.12em] align-super text-[0.42em] font-normal tracking-normal">™</span>
              </Link>
              <LumenMark onClick={() => setOpen(false)} />
            </div>
            <button
              data-testid="header-menu-close"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="flex h-11 w-11 items-center justify-center text-[#F5F5F7] transition-colors duration-200 hover:text-[#84DFF4]"
            >
              <X size={22} />
            </button>
          </div>
          <nav
            data-testid="header-mobile-nav"
            aria-label="Floor plan"
            className="mx-auto grid w-full max-w-6xl flex-1 content-center gap-x-10 gap-y-10 px-6 py-10 pb-[max(2.5rem,env(safe-area-inset-bottom))] sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-14"
          >
            {GROUPS.map((g) => (
              <div key={g.room} data-testid={`header-group-${g.room.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex flex-col">
                <span className="font-mono-spec text-[0.56rem] tracking-[0.3em] text-[#8E8E93]/80">{g.numeral}</span>
                <span className="type-eyebrow mt-1 !text-[#F5F5F7]/55">{g.room}</span>
                <span aria-hidden="true" className="mt-4 h-px w-8 bg-[#8E8E93]/35" />
                <div className="mt-4 flex flex-col">
                  {g.links.map((l) => (
                    <Link
                      key={l.to}
                      data-testid={l.testid}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="inline-flex min-h-[44px] items-center font-display text-xl font-light tracking-[0.02em] text-[#F5F5F7]/85 transition-colors duration-200 hover:text-[#84DFF4] sm:text-2xl"
                    >
                      {l.label.includes(" (") ? (
                        <span>{l.label.slice(0, l.label.indexOf(" ("))} <span className="whitespace-nowrap">{l.label.slice(l.label.indexOf(" (") + 1)}</span></span>
                      ) : l.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div data-testid="header-mobile-auth" className="mt-auto flex shrink-0 justify-center px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <AuthControl mobile />
          </div>
        </div>
      )}
    </header>
  );
}
